import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_ENDPOINTS, buildApiUrl } from '../../config/apiConfig';
import AdminHubPageFrame from '../../components/admin/AdminHubPageFrame';
import AdminSectionHeader from '../../components/admin/AdminSectionHeader';
import AdminDataTable from '../../components/admin/AdminDataTable';
import AdminTableToolbar from '../../components/admin/AdminTableToolbar';
import AdminJsonDetails from '../../components/admin/AdminJsonDetails';
import AdminEmptyState from '../../components/admin/AdminEmptyState';
import { adminPlanTableRows } from '../../admin/adminHubPresentation';
import { copyTextToClipboard, exportAdminTableAsCsv, exportAdminTableAsJson, normalizeAdminExportRows } from '../../admin/adminExportUtils';
import { filterAdminTableRows, nextSortState, sortAdminTableRows } from '../../admin/adminTableClient';

export default function AdminCommercialPlansPage() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [search, setSearch] = useState('');
  const [fPlan, setFPlan] = useState('');
  const [fAudience, setFAudience] = useState('');
  const [fActive, setFActive] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(null);
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN_COMMERCIAL_PLAN_CATALOG), {
          credentials: 'include',
        });
        if (!res.ok) throw new Error('bad');
        const json = await res.json();
        if (!cancel) {
          setData(json);
          setErr(false);
        }
      } catch {
        if (!cancel) setErr(true);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const snapshot = data?.snapshot;
  const rows = useMemo(() => adminPlanTableRows(snapshot), [snapshot]);

  const columns = useMemo(
    () => [
      { key: 'plan_code', header: t('adminKit.colPlanCode') },
      { key: 'audience_type', header: t('adminKit.colAudience') },
      { key: 'monthly_price', header: t('adminKit.colMonthly') },
      { key: 'annual_price', header: t('adminKit.colAnnual') },
      { key: 'currency', header: t('adminKit.colCurrency') },
      { key: 'is_active', header: t('adminKit.colActive') },
      { key: 'limits_summary', header: t('adminKit.colLimitsSummary') },
      { key: 'ai_summary', header: t('adminKit.colAiSummary') },
      { key: 'firm_mode_hint', header: t('adminKit.colFirmHint') },
      { key: 'metadata_preview', header: t('adminKit.colMetadataPreview') },
    ],
    [t]
  );

  const columnKeys = useMemo(() => columns.map((c) => c.key), [columns]);

  const displayRowsFinal = useMemo(() => {
    let out = filterAdminTableRows(rows, columnKeys, search, {
      plan_code: fPlan,
      audience_type: fAudience,
    });
    if (fActive === 'active') out = out.filter((r) => r.is_active === 'active');
    else if (fActive === 'inactive') out = out.filter((r) => r.is_active === 'inactive');
    else if (fActive === 'catalog_fallback') out = out.filter((r) => r.is_active === 'catalog_fallback');
    return sortAdminTableRows(out, sortKey, sortDir);
  }, [rows, columnKeys, search, fPlan, fAudience, fActive, sortKey, sortDir]);

  const handleSort = useCallback(
    (key) => {
      const n = nextSortState(sortKey, sortDir, key);
      setSortKey(n.key);
      setSortDir(n.dir);
    },
    [sortKey, sortDir]
  );

  const crumbs = useMemo(
    () => [
      { label: t('adminHub.navCommercial'), to: '/admin/commercial/plans' },
      { label: t('adminHub.navPlans') },
    ],
    [t]
  );

  const activeOptions = useMemo(
    () => [
      { value: '', label: t('adminExport.filterAll') },
      { value: 'active', label: t('adminExport.filterActiveLike') },
      { value: 'inactive', label: t('adminExport.filterInactive') },
      { value: 'catalog_fallback', label: t('adminExport.filterCatalogFallback') },
    ],
    [t]
  );

  const hasFilters = !!(search.trim() || fPlan.trim() || fAudience.trim() || fActive);

  return (
    <AdminHubPageFrame
      titleKey="adminHub.commercialPlansTitle"
      descKey="adminHub.commercialPlansDesc"
      breadcrumbItems={crumbs}
    >
      <section className="admin-section-card">
        {err ? (
          <div className="admin-state admin-state--error" role="status">
            <p>{t('adminHub.loadError')}</p>
          </div>
        ) : !data ? (
          <div className="admin-state admin-state--loading" role="status">
            <p>{t('adminPage.loading')}</p>
          </div>
        ) : (
          <div className="admin-hub-kit-stack">
            <AdminSectionHeader title={t('adminKit.planCatalogTableTitle')} subtitle={t('adminKit.planCatalogTableSub')} />
            {rows.length ? (
              <>
                <AdminTableToolbar
                  search={search}
                  onSearchChange={setSearch}
                  filteredCount={displayRowsFinal.length}
                  totalCount={rows.length}
                  compact={compact}
                  onCompactChange={setCompact}
                  onExportCsv={() => exportAdminTableAsCsv(displayRowsFinal, columnKeys, 'plan-catalog')}
                  onExportJson={() => exportAdminTableAsJson(displayRowsFinal, columnKeys, 'plan-catalog')}
                  onCopyFilteredJson={() =>
                    copyTextToClipboard(JSON.stringify(normalizeAdminExportRows(displayRowsFinal, columnKeys)))
                  }
                  filters={[
                    {
                      id: 'plan',
                      label: t('adminKit.colPlanCode'),
                      value: fPlan,
                      onChange: setFPlan,
                    },
                    {
                      id: 'audience',
                      label: t('adminKit.colAudience'),
                      value: fAudience,
                      onChange: setFAudience,
                    },
                    {
                      id: 'active',
                      label: t('adminKit.colActive'),
                      value: fActive,
                      onChange: setFActive,
                      options: activeOptions,
                    },
                  ]}
                />
                <AdminDataTable
                  columns={columns}
                  rows={displayRowsFinal}
                  emptyMessage={hasFilters ? t('adminExport.noResults') : t('adminKit.tableEmpty')}
                  emptyVariant={hasFilters ? 'noResults' : 'default'}
                  compact={compact}
                  sortable
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSortChange={handleSort}
                  copyRowKeys={columnKeys}
                />
              </>
            ) : (
              <AdminEmptyState message={t('adminKit.tableEmpty')} />
            )}
            <AdminJsonDetails title={t('adminKit.rawJsonToggle')} data={data} />
          </div>
        )}
      </section>
    </AdminHubPageFrame>
  );
}
